import React, { useState } from "react";
import { useApiSettings } from "@/hooks/useApiSettings";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  Save,
  Check,
  ExternalLink,
  ChevronDown,
  Key
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

interface ApiSettingsProps {
  userId?: string;
}

export const ApiSettings: React.FC<ApiSettingsProps> = ({ userId }) => {
  const { t } = useTranslation();
  const {
    apiKey,
    webhookUrl,
    webhookSecret,
    isLoading,
    isRegeneratingApiKey,
    isRegeneratingWebhookSecret,
    isUpdatingWebhookUrl,
    handleRegenerateApiKey,
    handleRegenerateWebhookSecret,
    handleUpdateWebhookUrl,
  } = useApiSettings(userId);

  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [localWebhookUrl, setLocalWebhookUrl] = useState("");
  const [isApiKeyCopied, setIsApiKeyCopied] = useState(false);
  const [isSecretCopied, setIsSecretCopied] = useState(false);

  const apiDocsUrl = import.meta.env.VITE_API_DOCS_URL || "https://api.voice2doc.com/api/v2/docs";

  // Sync local URL with data from hook
  React.useEffect(() => {
    if (webhookUrl) {
      setLocalWebhookUrl(webhookUrl);
    }
  }, [webhookUrl]);

  const copyToClipboard = async (text: string, type: 'api_key' | 'secret') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'api_key') {
        setIsApiKeyCopied(true);
        setTimeout(() => setIsApiKeyCopied(false), 2000);
      } else {
        setIsSecretCopied(true);
        setTimeout(() => setIsSecretCopied(false), 2000);
      }
      toast({
        title: t('profile.api.copied_title'),
        description: type === 'api_key' ? t('profile.api.key_copied') : t('profile.api.secret_copied'),
      });
    } catch (err) {
      toast({
        title: t('profile.api.copy_error_title'),
        description: t('profile.api.copy_error_desc'),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 bg-brand-100 text-brand-500 rounded-full flex items-center justify-center">
            <Key size={20} />
          </div>
          <h2 className="text-xl font-medium">{t('profile.api.title')}</h2>
        </div>
        <div className="space-y-4">
          <div className="h-10 bg-muted animate-pulse rounded" />
          <div className="h-10 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-brand-100 text-brand-500 rounded-full flex items-center justify-center">
            <Key size={20} />
          </div>
          <div>
            <h2 className="text-xl font-medium">{t('profile.api.title')}</h2>
            {!isExpanded && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('profile.api.subtitle')}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-10 w-10"
        >
          <ChevronDown
            size={20}
            className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('profile.api.description')}
            </p>

            <a
              href={apiDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary flex items-center gap-1 hover:underline"
            >
              {t('profile.api.docs_link')}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="api-key">{t('profile.api.key_label')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="api-key"
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  readOnly
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="sr-only">
                    {showApiKey ? t('profile.api.hide_key') : t('profile.api.show_key')}
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(apiKey, 'api_key')}
                title={t('profile.api.copy_tooltip')}
              >
                {isApiKeyCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" disabled={isRegeneratingApiKey} title={t('profile.api.regenerate_key_tooltip')}>
                    <RefreshCw className={`h-4 w-4 ${isRegeneratingApiKey ? "animate-spin" : ""}`} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('profile.api.regenerate_key_title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('profile.api.regenerate_key_desc')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('profile.api.cancel_btn')}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleRegenerateApiKey()}>
                      {t('profile.api.regenerate_btn')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Webhook URL */}
          <div className="space-y-2">
            <Label htmlFor="webhook-url">{t('profile.api.webhook_url_label')}</Label>
            <div className="flex gap-2">
              <Input
                id="webhook-url"
                type="url"
                placeholder="https://your-domain.com/webhook"
                value={localWebhookUrl}
                onChange={(e) => setLocalWebhookUrl(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleUpdateWebhookUrl(localWebhookUrl)}
                disabled={isUpdatingWebhookUrl || localWebhookUrl === webhookUrl}
                title={t('profile.api.save_url_tooltip')}
              >
                <Save className={`h-4 w-4 ${isUpdatingWebhookUrl ? "animate-pulse" : ""}`} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('profile.api.webhook_url_desc')}
            </p>
          </div>

          {/* Webhook Secret */}
          <div className="space-y-2">
            <Label htmlFor="webhook-secret">{t('profile.api.webhook_secret_label')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="webhook-secret"
                  type={showWebhookSecret ? "text" : "password"}
                  value={webhookSecret}
                  readOnly
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                >
                  {showWebhookSecret ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="sr-only">
                    {showWebhookSecret ? t('profile.api.hide_secret') : t('profile.api.show_secret')}
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(webhookSecret, 'secret')}
                title={t('profile.api.copy_tooltip')}
              >
                {isSecretCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" disabled={isRegeneratingWebhookSecret} title={t('profile.api.regenerate_secret_tooltip')}>
                    <RefreshCw className={`h-4 w-4 ${isRegeneratingWebhookSecret ? "animate-spin" : ""}`} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('profile.api.regenerate_secret_title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('profile.api.regenerate_secret_desc')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('profile.api.cancel_btn')}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleRegenerateWebhookSecret()}>
                      {t('profile.api.regenerate_btn')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('profile.api.webhook_secret_desc')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

